import { useEffect, useState } from "react";
import { useGlobal } from "../../contexts/globalContext";
import { useHistory, withRouter } from "react-router";
import GlobalLoader from "../../components/GlobalLoader";
import { useCampaign } from "../../hooks/useCampaign";
import VitrineRepository from "../../repositories/vitrineRepository";
import { storage } from "../../services/firebase";

function Guest() {
	const { loginGuestByCampaignCode, signed } = useGlobal();
	let history = useHistory();
	const { campaignId, campaignCode, getCampaignColors } = useCampaign();
	const [loadingConfig, setLoadingConfig] = useState(false);
	const [pendingExperienceId, setPendingExperienceId] = useState(null);
	const [pendingSearch, setPendingSearch] = useState(false); // Deep link para busca geral
	const [authReady, setAuthReady] = useState(false);

	// ========================================
	// Monitora quando o token de autenticação fica disponível
	// ========================================
	useEffect(() => {
		const checkAuth = () => {
			const auth = sessionStorage.getItem('auth');
			if (auth && !authReady) {
				console.log('[DEBUG] Token de autenticação detectado! Aguardando estabilização...');
				// Delay de 400ms para garantir que token está válido no servidor
				setTimeout(() => {
					console.log('[DEBUG] Token pronto para uso!');
					setAuthReady(true);
				}, 400);
			}
		};

		// Verifica imediatamente
		checkAuth();

		// Verifica a cada 100ms até o auth aparecer
		const interval = setInterval(() => {
			if (!authReady) {
				checkAuth();
			} else {
				clearInterval(interval);
			}
		}, 100);

		return () => clearInterval(interval);
	}, [authReady]);

	// ========================================
	// DEEP LINKING: Escuta postMessage do site institucional
	// Site abre o portal e envia experienceId via window.postMessage
	// ========================================
	useEffect(() => {
		const handleMessage = (event) => {
			// Valida origem (aceita HML e PRD do site institucional)
			const allowedOrigins = [
				'https://site-hml.destinoferias.com.br',
				'https://destinoferias.com.br',
				'https://destinoferias-prod-site-279xs.ondigitalocean.app', // Digital Ocean (antes do DNS)
				'http://localhost:3000', // Para testes locais
			];

			if (!allowedOrigins.includes(event.origin)) {
				console.log('[DEBUG] PostMessage ignorado - origem não autorizada:', event.origin);
				return;
			}

			// Verifica tipo da mensagem
			if (event.data?.type === 'DEEP_LINK_EXPERIENCE') {
				const experienceId = event.data.experienceId;
				console.log('[DEBUG] Deep link recebido via postMessage! experienceId:', experienceId);
				setPendingExperienceId(experienceId);
			} else if (event.data?.type === 'DEEP_LINK_EXPERIENCE_SEARCH') {
				console.log('[DEBUG] Deep link recebido para busca geral de experiências!');
				setPendingSearch(true);
			}
		};

		// Registra listener
		window.addEventListener('message', handleMessage);
		console.log('[DEBUG] Listener de postMessage registrado');

		// Cleanup
		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, []);

	// ========================================
	// DEEP LINKING: Busca experiência APÓS login completar
	// Usa a MESMA lógica do ShowCaseLine/index.js (linha 115-158)
	// ========================================
	useEffect(() => {
		// Só busca se:
		// 1. Tem experienceId pendente
		// 2. Campanha está carregada
		// 3. Config carregada
		// 4. TOKEN está disponível (authReady = true)
		// 5. LOGIN completado (signed = true) - garante contexto global pronto
		if (!pendingExperienceId || !campaignId || !campaignCode || loadingConfig || !authReady || !signed) {
			if (pendingExperienceId && !authReady) {
				console.log('[DEBUG] Aguardando token de autenticação...');
			}
			if (pendingExperienceId && authReady && !signed) {
				console.log('[DEBUG] Aguardando login completar (signed = true)...');
			}
			return;
		}

		const handleDeepLink = async () => {
			console.log('[DEBUG] Login completado! Token disponível. Buscando experiência:', pendingExperienceId);

			// Busca imagens do Firebase Storage (mesma lógica do ShowCaseLine)
			const loadExperienceImages = (expId) => {
				return storage
					.ref()
					.child(`experience/${expId}`)
					.listAll()
					.then((res) =>
						Promise.all(res.items.map((itemRef) => itemRef.getDownloadURL()))
					)
					.catch(() => []);
			};

			try {
				const vitrineRepository = new VitrineRepository();

				// Busca dados da experiência via API (mesma lógica do ShowCaseLine)
				const res = await vitrineRepository.getExperienceById(pendingExperienceId);
				const experience = res?.data?.experience;

				if (!experience) {
					console.error('[ERRO] Experiência não encontrada');
					setPendingExperienceId(null);
					return;
				}

				console.log('[DEBUG] Experiência carregada:', experience);

				// Carrega imagens do Firebase
				const images = await loadExperienceImages(experience.id);
				console.log('[DEBUG] Imagens carregadas:', images.length);

				// Salva no SessionStorage (MESMO FORMATO que ShowCaseLine usa)
				sessionStorage.setItem(
					'@experience-details',
					JSON.stringify({
						experience,
						images,
						serverTime: res?.data?.serverTime || null,
					})
				);
				sessionStorage.setItem('buscaAtual', 'Experiencias');
				// SubHeaderSearch espera 'experienceSearch' com estrutura de busca
				sessionStorage.setItem(
					'experienceSearch',
					JSON.stringify({ categoria: '', subcategoria: '', subcategorias: [], cidade: '', datas: '' })
				);

				// Redireciona SEM limpar pendingExperienceId (evita race condition com useEffect da home)
				console.log('[DEBUG] Redirecionando para página de detalhes...');
				history.push('/busca/experience-details');
			} catch (error) {
				console.error('[ERRO] Falha no deep linking:', error);
				console.error('[ERRO] Stack:', error);
				setPendingExperienceId(null);
			}
		};

		handleDeepLink();
	}, [pendingExperienceId, campaignId, campaignCode, loadingConfig, authReady, signed, history]);
	// ========================================

	// ========================================
	// DEEP LINKING: Busca geral de experiências
	// ========================================
	useEffect(() => {
		// Aguarda login completar antes de redirecionar
		if (!pendingSearch || !campaignId || !campaignCode || loadingConfig || !authReady || !signed) {
			if (pendingSearch && !signed) {
				console.log('[DEBUG] Aguardando login completar para busca geral...');
			}
			return;
		}

		console.log('[DEBUG] Login completado! Redirecionando para busca geral de experiências...');

		// Salva estrutura vazia de busca (sem filtros = todas as experiências)
		sessionStorage.setItem('buscaAtual', 'Experiencias');
		sessionStorage.setItem(
			'experienceSearch',
			JSON.stringify({ categoria: '', subcategoria: '', subcategorias: [], cidade: '', datas: '' })
		);

		// Redireciona para página de resultados
		history.push('/busca/resultado-pesquisa');
	}, [pendingSearch, campaignId, campaignCode, loadingConfig, authReady, signed, history]);
	// ========================================

	useEffect(() => {
		// Só redireciona para home se NÃO tem deep link pendente
		if (signed && !pendingExperienceId && !pendingSearch) {
			history.push("/");
		}
	}, [signed, pendingExperienceId, pendingSearch, history]);

	const loadConfig = async () => {
		await getCampaignColors(true);
	};

	useEffect(() => {
		let isMounted = true;
		if (campaignId && campaignCode) {
			loadConfig().then(() => {
				if (isMounted) setLoadingConfig(false);
			});
		}
		return () => {
			isMounted = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campaignId, campaignCode, getCampaignColors]);

	useEffect(() => {
		if (campaignId && campaignCode && !loadingConfig) {
			loginGuestByCampaignCode(campaignCode);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);


	return (
		<>
			<GlobalLoader />
		</>
	);
}
export default withRouter(Guest);
